/**
 * Which of the two apps this page is running inside.
 *
 * THE OWNER, 2026-10-10: *"i want ustool a seperate app altogether no one
 * should see others app"*. Until now both shells loaded m.hundai.tech and
 * the ROLE decided the screens, so Ustool opened on "Welcome to Qafila" and
 * a transporter signing into Qafila got the transporter tabs. Two apps, one
 * experience — which is both a confusing first run and the thing Apple
 * rejects two listings for (guideline 4.3).
 *
 * HOW IT IS DECIDED, in order:
 *
 *  0. THE HOSTNAME. ustool.hundai.tech is Ustool and m.hundai.tech is
 *     Qafila, full stop -- the owner asked for "two seperate url", and an
 *     address is the one signal a person can see, share and bookmark. It
 *     beats the injection because a shell that somehow loads the wrong host
 *     should show that host's app, not argue with it.
 *  1. `window.__APP_KIND__`, injected by the native shell BEFORE the page
 *     loads. This is the real answer in the apps. A page cannot talk its way
 *     out of it and it survives every navigation, unlike a path or a query
 *     string, which one tap on a link would lose.
 *  2. `?app=transporter`, remembered in SESSION storage. For opening either
 *     face in a desktop browser without building an APK.
 *
 *     Session, deliberately, not local. localStorage would mean that anyone
 *     who ever followed such a link turned m.hundai.tech into Ustool on that
 *     device for good -- and that URL is the whole product for people
 *     without an app installed. Session storage survives the reloads the
 *     sign-in flow does (`location.assign('/')`) and dies with the tab.
 *  3. Neither — `'web'`. m.hundai.tech in a browser keeps behaving exactly
 *     as it does today, both roles, because that URL is also the product
 *     for anyone without the app installed.
 */
export type AppKind = 'client' | 'transporter' | 'web'

const KEY = 'tb.appKind'

declare global {
  interface Window { __APP_KIND__?: string }
}

/** Hosts that ARE one app, whatever else says otherwise. */
const HOSTS: Record<string, AppKind> = {
  'ustool.hundai.tech': 'transporter',
  'm.hundai.tech': 'client',
}

function read(): AppKind {
  if (typeof window === 'undefined') return 'web'

  const byHost = HOSTS[window.location.hostname]
  if (byHost) return byHost

  // Then the shell's word, for a build pointed somewhere else. Never written
  // to storage: a browser that once opened ?app=transporter must not turn
  // into Ustool for good.
  const injected = window.__APP_KIND__
  if (injected === 'client' || injected === 'transporter') return injected

  try {
    const q = new URLSearchParams(window.location.search).get('app')
    if (q === 'client' || q === 'transporter') {
      sessionStorage.setItem(KEY, q)
      return q
    }
    const saved = sessionStorage.getItem(KEY)
    if (saved === 'client' || saved === 'transporter') return saved
  } catch { /* private window, blocked storage */ }

  return 'web'
}

/*
 * Read ONCE, at module load.
 *
 * It cannot change while the page is open -- the shell injects it before
 * first paint -- and a function that re-read it would invite a component to
 * render one app on mount and the other after a storage event.
 */
export const APP_KIND: AppKind = read()

/** Ustool. Passenger screens do not exist here. */
export const IS_USTOOL = APP_KIND === 'transporter'
/** Qafila. Transporter screens do not exist here. */
export const IS_QAFILA = APP_KIND === 'client'
/** m.hundai.tech in a browser: both, as before. */
export const IS_WEB = APP_KIND === 'web'

/** The brand this app wears, for the app bar and the welcome copy. */
export const BRAND_KEY = IS_USTOOL ? 'app.nameUstool' : 'app.name'

/** The other app's address, for the refusal screen to offer as a link. */
export const OTHER_URL = IS_USTOOL
  ? 'https://m.hundai.tech'
  : 'https://ustool.hundai.tech'
